/**
 * ImageExamples.tsx
 * Comprehensive examples of HeroUI Image component
 * 
 * Showcases all HeroUI Image features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Image } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';


export const ImageExamples: React.FC = () => {
  // Sample images for examples (using placeholder service)
  const sampleImages = {
    landscape: 'https://picsum.photos/600/400?random=1',
    portrait: 'https://picsum.photos/400/600?random=2',
    square: 'https://picsum.photos/400/400?random=3',
    small: 'https://picsum.photos/200/150?random=4',
    chart: 'https://picsum.photos/800/500?random=5',
    avatar: 'https://picsum.photos/100/100?random=6',
    fallback: 'https://picsum.photos/300/200?random=fallback',
    broken: 'https://invalid-url-for-testing.jpg'
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="text-center space-y-3">
                  <Image 
                    src={sampleImages.landscape}
                    alt="Landscape image"
                    width={200}
                    height={150}
                  />
                  <p className="text-sm text-text-secondary">Basic Image</p>
                </div>
                
                <div className="text-center space-y-3">
                  <Image 
                    src={sampleImages.square}
                    alt="Square image"
                    width={150}
                    height={150}
                  />
                  <p className="text-sm text-text-secondary">Square Format</p>
                </div>
                
                <div className="text-center space-y-3">
                  <Image 
                    src={sampleImages.small}
                    alt="Small image"
                    width={120}
                    height={90}
                  />
                  <p className="text-sm text-text-secondary">Small Size</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Radius Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Border Radius</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center space-y-2">
                  <Image 
                    src={sampleImages.square}
                    alt="No radius"
                    width={120}
                    height={120}
                    radius="none"
                  />
                  <p className="text-xs text-text-secondary">None</p>
                </div>
                
                <div className="text-center space-y-2">
                  <Image 
                    src={sampleImages.square}
                    alt="Small radius"
                    width={120}
                    height={120}
                    radius="sm"
                  />
                  <p className="text-xs text-text-secondary">Small</p>
                </div>
                
                <div className="text-center space-y-2">
                  <Image 
                    src={sampleImages.square}
                    alt="Medium radius"
                    width={120}
                    height={120}
                    radius="md"
                  />
                  <p className="text-xs text-text-secondary">Medium</p>
                </div>
                
                <div className="text-center space-y-2">
                  <Image 
                    src={sampleImages.square}
                    alt="Large radius"
                    width={120}
                    height={120}
                    radius="lg"
                  />
                  <p className="text-xs text-text-secondary">Large</p>
                </div>
                
                <div className="text-center space-y-2">
                  <Image 
                    src={sampleImages.avatar}
                    alt="Full radius (circular)"
                    width={120}
                    height={120}
                    radius="full"
                  />
                  <p className="text-xs text-text-secondary">Full (Circle)</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Visual Effects */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Visual Effects</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Blurred Effect */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Blurred Background</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="text-center">
                    <Image 
                      src={sampleImages.landscape}
                      alt="Blurred background effect"
                      width={280}
                      height={200}
                      isBlurred
                      radius="lg"
                    />
                  </div>
                  <p className="text-sm text-text-secondary text-center">
                    Creates an elegant blurred background effect
                  </p>
                </div>
              </CardBody>
            </Card>

            {/* Zoom Effect */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Zoom on Hover</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="text-center">
                    <Image 
                      src={sampleImages.chart}
                      alt="Hover to zoom"
                      width={280}
                      height={200}
                      isZoomed
                      radius="md"
                    />
                  </div>
                  <p className="text-sm text-text-secondary text-center">
                    Hover to see zoom effect (great for charts)
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Fallback Images */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Fallback & Error Handling</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center space-y-3">
                  <Image 
                    src={sampleImages.broken}
                    fallbackSrc={sampleImages.fallback}
                    alt="Image with fallback"
                    width={250}
                    height={180}
                    radius="md"
                  />
                  <p className="text-sm text-text-secondary">
                    Broken source with fallback image
                  </p>
                </div>
                
                <div className="text-center space-y-3">
                  <Image 
                    src={sampleImages.landscape}
                    fallbackSrc={sampleImages.fallback}
                    alt="Working image with fallback ready"
                    width={250}
                    height={180}
                    radius="md"
                  />
                  <p className="text-sm text-text-secondary">
                    Working image (fallback ready)
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Journal Context</h3>
          <div className="space-y-6">
            
            {/* Chart Screenshots */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Chart Screenshots</h4>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Image 
                      src={sampleImages.chart}
                      alt="Daily trading chart"
                      width={350}
                      height={250}
                      radius="md"
                      isZoomed
                      className="border border-border"
                    />
                    <div className="text-sm text-text-secondary">
                      <p><strong>Daily Analysis Chart</strong></p>
                      <p>Entry/Exit points with technical indicators</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Image 
                      src={sampleImages.landscape}
                      alt="Market overview"
                      width={350}
                      height={250}
                      radius="md"
                      isBlurred
                      className="border border-border"
                    />
                    <div className="text-sm text-text-secondary">
                      <p><strong>Market Overview</strong></p>
                      <p>Sector performance and market sentiment</p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Profile & Avatar Images */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Profile & Avatars</h4>
              </CardHeader>
              <CardBody>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="text-center space-y-2">
                    <Image 
                      src={sampleImages.avatar}
                      alt="Trader profile"
                      width={80}
                      height={80}
                      radius="full"
                    />
                    <p className="text-xs text-text-secondary">Large Avatar</p>
                  </div>
                  
                  <div className="text-center space-y-2">
                    <Image 
                      src={sampleImages.avatar}
                      alt="Trader profile"
                      width={50}
                      height={50}
                      radius="full"
                    />
                    <p className="text-xs text-text-secondary">Medium Avatar</p>
                  </div>
                  
                  <div className="text-center space-y-2">
                    <Image 
                      src={sampleImages.avatar}
                      alt="Trader profile"
                      width={32}
                      height={32}
                      radius="full"
                    />
                    <p className="text-xs text-text-secondary">Small Avatar</p>
                  </div>
                  
                  <div className="ml-6 space-y-1">
                    <p className="text-sm font-medium text-text-primary">Profile Integration</p>
                    <p className="text-xs text-text-secondary">
                      Perfect for user profiles, broker logos, and team member photos
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Responsive & Loading Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Responsive & Loading States</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Responsive Images</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Image 
                      src={sampleImages.landscape}
                      alt="Responsive image 1"
                      width={300}
                      height={200}
                      radius="md"
                      className="w-full h-auto"
                    />
                    <Image 
                      src={sampleImages.square}
                      alt="Responsive image 2"
                      width={300}
                      height={300}
                      radius="md"
                      className="w-full h-auto"
                    />
                    <Image 
                      src={sampleImages.portrait}
                      alt="Responsive image 3"
                      width={200}
                      height={300}
                      radius="md"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-sm text-text-secondary mt-2">
                    Images adapt to container width while maintaining aspect ratio
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Loading Strategies</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Image 
                        src={sampleImages.chart}
                        alt="Eager loading"
                        width={250}
                        height={180}
                        radius="md"
                        loading="eager"
                      />
                      <p className="text-sm text-text-secondary">
                        <strong>Eager Loading:</strong> Load immediately (above fold content)
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <Image 
                        src={sampleImages.landscape}
                        alt="Lazy loading"
                        width={250}
                        height={180}
                        radius="md"
                        loading="lazy"
                      />
                      <p className="text-sm text-text-secondary">
                        <strong>Lazy Loading:</strong> Load when entering viewport (default)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Advanced Combinations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Combinations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Combined Effects</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="text-center space-y-2">
                      <Image 
                        src={sampleImages.chart}
                        alt="Chart with combined effects"
                        width={180}
                        height={140}
                        radius="lg"
                        isBlurred
                        isZoomed
                        className="border-2 border-primary"
                      />
                      <p className="text-xs text-text-secondary">Blurred + Zoom + Border</p>
                    </div>
                    
                    <div className="text-center space-y-2">
                      <Image 
                        src={sampleImages.avatar}
                        alt="Profile with effects"
                        width={140}
                        height={140}
                        radius="full"
                        isZoomed
                        className="ring-4 ring-success ring-offset-2 ring-offset-background"
                      />
                      <p className="text-xs text-text-secondary">Avatar + Ring + Zoom</p>
                    </div>
                    
                    <div className="text-center space-y-2">
                      <Image 
                        src={sampleImages.landscape}
                        fallbackSrc={sampleImages.fallback}
                        alt="All features combined"
                        width={180}
                        height={140}
                        radius="md"
                        isBlurred
                        className="shadow-lg hover:shadow-xl transition-shadow"
                      />
                      <p className="text-xs text-text-secondary">Fallback + Blur + Shadow</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-default-100/50 dark:bg-default-800/50 rounded-lg">
                  <p className="text-sm text-text-secondary">
                    <strong>Performance Tip:</strong> Use appropriate loading strategies and image sizes for optimal performance. 
                    The Image component includes automatic skeleton loading states and lazy loading by default.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};